// Maps a visitor's detected country to the nearest of our actually-served
// regions (pipeline codes) when there's no exact-match pipeline for their
// own country. Used only as a fallback in Header.jsx's first-visit region
// auto-select — a direct country->pipeline match always wins over this.
//
// Grouped by rough geography/travel-hub proximity to whichever of our
// regions is closest. Anything not listed here falls through to the
// existing India default (unchanged prior behavior).

// Oceania / Pacific -> Australia
const NEAREST_TO_AU = [
  "NZ", "FJ", "PG", "WS", "TO", "VU", "SB", "NC", "PF",
  "GU", "KI", "FM", "MH", "PW", "NR", "TV", "CK", "NU",
];

// Middle East / Gulf / nearby -> UAE
const NEAREST_TO_AE = [
  "SA", "QA", "KW", "OM", "BH", "IQ", "JO", "LB", "IL", "PS",
  "SY", "YE", "IR", "EG", "TR", "AZ", "GE", "AM",
  "UZ", "KZ", "KG", "TJ", "TM",
];

// South Asia (excluding India/Pakistan themselves, which already exact-match) -> India
const NEAREST_TO_IN = ["BD", "LK", "NP", "BT", "MV"];

// Afghanistan sits right on Pakistan's border -> Pakistan
const NEAREST_TO_PK = ["AF"];

const NEAREST_REGION_BY_COUNTRY = {};
NEAREST_TO_AU.forEach((code) => (NEAREST_REGION_BY_COUNTRY[code] = "AU"));
NEAREST_TO_AE.forEach((code) => (NEAREST_REGION_BY_COUNTRY[code] = "AE"));
NEAREST_TO_IN.forEach((code) => (NEAREST_REGION_BY_COUNTRY[code] = "IN"));
NEAREST_TO_PK.forEach((code) => (NEAREST_REGION_BY_COUNTRY[code] = "PK"));

// Continent-level fallback. `continent_code` (from ipapi.co) is one of only
// seven values, so unlike the country list above this one genuinely covers
// every country on earth — it's what catches the countries we haven't (and
// can't practically) enumerate one by one above. Only reached when the
// country itself wasn't in NEAREST_REGION_BY_COUNTRY.
const NEAREST_REGION_BY_CONTINENT = {
  OC: "AU", // Oceania
  AS: "IN", // Asia — Middle East subset already overridden to AE above
  EU: "AE", // Europe — closer to the Gulf hub than to India/Australia
  AF: "AE", // Africa — Gulf hub is the common long-haul connection point
  NA: "AE", // North America — long-haul, typically connects via a Gulf hub
  SA: "AE", // South America — same reasoning as North America
  AN: "AU", // Antarctica — Australia is the nearest served region
};

/**
 * Given the visitor's detected country/continent code, returns the pipeline
 * code of the nearest served region, or null if nothing matches (caller
 * should fall back to India). continentCode should always be present
 * (ipapi.co returns it for every country), so this only returns null when
 * geolocation itself failed.
 */
export function getNearestRegionCode(countryCode, continentCode) {
  if (countryCode && NEAREST_REGION_BY_COUNTRY[countryCode]) {
    return NEAREST_REGION_BY_COUNTRY[countryCode];
  }
  if (continentCode && NEAREST_REGION_BY_CONTINENT[continentCode]) {
    return NEAREST_REGION_BY_CONTINENT[continentCode];
  }
  return null;
}
